<template>
  <div class="visual_menu--section menu--section" :data-section-id="section.ms_id" :id="section.ms_id + '-MS'"
       :data-menu-section-component="section.ms_custom_flipbook_component"
       :class="containerClasses" :style="containerStyles"
       @touchmove="handleTouchMove" @touchend="handleTouchEnd">

    <slot name="sectionTitleImage"></slot>

    <menu-content-area v-if="'above-title' === contentAreaTopLayout" :content="section.ms_content_area_top"
                       type="section" position="top"></menu-content-area>

    <h2 v-if="sectionTitle.length" class="menu--section_title">
      <span v-if="isTitleBufferVisible"></span>
      <span v-html="sectionTitle"></span>
      <span v-if="isTitleBufferVisible"></span>
      <span v-if="isTitleBoxVisible" class="menu--section_title_box"></span>
    </h2>
    <h3 v-if="sectionMenuType.length" v-html="sectionMenuType" class="menu--section_menu_type"></h3>
    <h3 v-if="!sectionMenuType.length && sectionSubtitle.length" v-html="sectionSubtitle" class="menu--section_subtitle"></h3>
    <h4 v-if="sectionMenuType.length && sectionSubtitle.length" v-html="sectionSubtitle" class="menu--section_subtitle"></h4>

    <menu-schedules v-if="$store.state.isAdminView && section.normalizedSchedules.length"
                    :normalized-schedules="section.normalizedSchedules"></menu-schedules>

    <slot name="beforeTopContentArea"></slot>

    <menu-content-area v-if="'' === contentAreaTopLayout" :content="section.ms_content_area_top" type="section"
                       position="top"></menu-content-area>

    <menu-item-group :section="section" :menu-item-component="menuItemComponent"
                     :menu-item-tag-location="menuItemTagLocation"
                     :layout="itemGroupLayout"></menu-item-group>

    <slot name="beforeBottomContentArea"></slot>

    <div
      v-if="('none' !== contentAreaBottomLayout && section.ms_content_area_bottom.length) || section.section_tags.length"
      class="visual_menu--page_section visual_menu--page_section visual_menu--page_section-bottom">
      <menu-content-area v-if="'none' !== contentAreaBottomLayout" :content="section.ms_content_area_bottom"
                         type="section" position="bottom"
                         class="viewport"></menu-content-area>
      <menu-tag-legend v-if="section.section_tags.length" :tags="section.section_tags"></menu-tag-legend>
    </div>

    <menu-version v-if="!isTagLegendHidden && section.ms_menu_version_override"
                  :version="section.ms_menu_version_override"></menu-version>
  </div>
</template>

<script lang="ts">
/**
 * Custom component for Bimini default menu sections.
 *
 * @author Jasper Howard <jhoward@peppermillcas.com>
 * @version 1.4.0
 * @copyright  Copyright (C) 2023-2026, Peppermill Inc. All rights reserved. Redistribution and use in source and binary forms, with or without modification, are not permitted.
 * @changelog
 *
 *  2023-06-07 (1.0.0) (jhoward)
 *    - Copy from PanchoWillies_defaultSection.vue component to Chi_defaultSection.vue component.
 *
 *  2023-08-18 (1.0.1) (jhoward)
 *    - Add custom intro page CSS to <Chi_defaultSection>.
 *
 *  2023-08-28 (1.1.0) (jhoward)
 *    - Use <menu-version> in <Chi_defaultSection>.
 *
 *  2025-02-11 (1.2.0) (jhoward)
 *    - Add <menu-schedules> to the <Chi_defaultSection> component if in admin view.
 *
 *  2025-02-11 (1.3.0) (jhoward)
 *    - Add touch zoom to section components in <defaultVisualSection>.
 *
 * 2026-02-17 (1.3.1) (jhoward)
 *   - Add beforeBottomContentArea slot to App\Library\Js\Components\Menu\Section\Visual\defaultVisualSection.vue for custom content injection.
 *
 * 2026-07-20 (1.4.0) (jhoward)
 *   - Add beforeBottomContentArea slot to App\Library\Js\Components\Menu\Section\Visual\defaultVisualSection.vue.
 */

import MenuTagLegend from "../../menu-tag-legend.vue";
import {
  computeItemGroupPricingTypes,
  computeSectionSubtitle,
  computeSectionTitle
} from "../../../../helpers/menu-sections";
import MenuContentArea from "../../menu-content-area.vue";
import {defineComponent, PropType} from "vue";
import {
  ALLOWED_ITEM_COMPONENTS,
  Menu,
  MENU_ITEM_GROUP_LAYOUT,
  MENU_ITEM_TAG_LOCATIONS,
  MenuSection, SECTION_CONTENT_AREA_BOTTOM_LAYOUT,
  SECTION_CONTENT_AREA_TOP_LAYOUT
} from "../../defs";
import MenuItemGroup from "../../menu-item-group.vue";
import MenuVersion from "../../menu-version.vue";
import MenuSchedules from "../../schedule/MenuSchedules.vue";

export default defineComponent({
  components: {
    MenuSchedules,
    MenuVersion,
    MenuItemGroup,
    MenuContentArea,
    MenuTagLegend,
  },
  name: "default-visual-section",
  props: {
    menu: {
      type: Object as PropType<Menu>,
      required: true
    },
    section: {
      type: Object as PropType<MenuSection>,
      required: true
    },
    sectionModifier: {
      type: String,
      default: ''
    },
    menuItemComponent: {
      type: String,
      required: true,
      validate: (value: string) => '' === value || ALLOWED_ITEM_COMPONENTS.indexOf(value)
    },
    menuItemTagLocation: {
      type: String as PropType<MENU_ITEM_TAG_LOCATIONS>,
      default: ''
    },
    itemGroupLayout: {
      type: String as PropType<MENU_ITEM_GROUP_LAYOUT>,
      default: ''
    },
    contentAreaTopLayout: {
      type: String as PropType<SECTION_CONTENT_AREA_TOP_LAYOUT>,
      default: ''
    },
    contentAreaBottomLayout: {
      type: String as PropType<SECTION_CONTENT_AREA_BOTTOM_LAYOUT>,
      default: ''
    },
    isTagLegendHidden: {
      type: Boolean,
      default: false
    },
    isTitleBoxVisible: {
      type: Boolean,
      default: false
    },
    isTitleBufferVisible: {
      type: Boolean,
      default: false
    },
    sectionMenuType: {
      type: String,
      default: ''
    },
  },
  data: function () {
    return {
      isAnimateScale: false,
      currentScale: 1,
      scaleSave: 1,
      originalXOffset: null as null | number,
      originalYOffset: null as null | number,
      originalXCoordinate: null as null | number,
      originalYCoordinate: null as null | number,
      menuSectionNodeBoundingRect: null as null | DOMRect,

      touchResetTimer: -1,
      touchZoomTimeout: 5000,

      touchZoomMaxScale: 1.5,
      touchZoomMinScale: 1,

      touchZoomOffsetX: 0,
      touchZoomOffsetY: 0,
    };
  },
  computed: {
    itemGroupPricingTypes: computeItemGroupPricingTypes,
    sectionTitle: computeSectionTitle,
    sectionSubtitle: computeSectionSubtitle,

    containerClasses: function () {
      return {
        'menu--section-show_bin': !!this.section.ms_show_bin,
        'menu--section-show_pricing_header': !!this.section.ms_show_pricing_header,
        'menu--section-group_by_price': !!this.section.ms_group_by_price,
        'menu--section-highlight': !!this.section.ms_highlight,
        'menu--section-desserts': this.section.ms_title.toLowerCase() === 'desserts',
        'visual_menu--section-animate_scale': this.isAnimateScale,
      };
    },

    containerStyles(): {[key: string]: string} {
      return {
        '--zoom-scale': '' + this.currentScale,
        '--zoom-offset-x': '' + this.touchZoomOffsetX + 'px',
        '--zoom-offset-y': '' + this.touchZoomOffsetY + 'px',
      };
    },
  },
  methods: {
    getFormattedPrice: function (price: string): string {
      return price.replace(/\$/g, '');
    },

    handleTouchMove(event: TouchEvent) {
      this.isAnimateScale = false;

      if (null === this.menuSectionNodeBoundingRect) {
        this.menuSectionNodeBoundingRect = this.$el.getBoundingClientRect();
      }

      if ('scale' in event && 1 < event.touches.length) {
        window.clearTimeout(this.touchResetTimer);

        this.currentScale = Math.max(this.touchZoomMinScale, Math.min(this.touchZoomMaxScale, this.scaleSave + (event.scale as number) - 1));

        this.touchZoomOffsetX = ((event.touches[0].clientX + event.touches[1].clientX) / 2) - this.menuSectionNodeBoundingRect.left;
        this.touchZoomOffsetY = ((event.touches[0].clientY + event.touches[1].clientY) / 2) - this.menuSectionNodeBoundingRect.top;
      } else if (1 === event.touches.length) {
        const offsetX = event.touches[0].clientX - this.menuSectionNodeBoundingRect.left;
        const offsetY = event.touches[0].clientY - this.menuSectionNodeBoundingRect.top;

        if (null === this.originalXOffset) {
          this.originalXOffset = this.touchZoomOffsetX;
          this.originalYOffset = this.touchZoomOffsetY;
          this.originalXCoordinate = offsetX;
          this.originalYCoordinate = offsetY;
        }
        const distanceX = (this.originalXCoordinate || 0) - offsetX;
        const distanceY = (this.originalYCoordinate || 0) - offsetY;

        this.touchZoomOffsetX = ((this.originalXOffset || 0) + distanceX);
        this.touchZoomOffsetY = ((this.originalYOffset || 0) + distanceY);
        window.clearTimeout(this.touchResetTimer);
      }
    },
    handleTouchEnd(event: TouchEvent) {
      if ('scale' in event && 0 < event.touches.length) {
        this.scaleSave = this.currentScale;
      }

      this.originalXOffset = null;
      this.originalYOffset = null;
      this.originalXCoordinate = null;
      this.originalYCoordinate = null;
      this.menuSectionNodeBoundingRect = null;
      this.setTouchResetTimer();
    },
    setTouchResetTimer() {
      window.clearTimeout(this.touchResetTimer);
      this.touchResetTimer = window.setTimeout(() => {
        this.scaleSave = 1;
        this.isAnimateScale = true;
        window.setTimeout(() => {
          this.currentScale = 1;
        }, 66);
      }, this.touchZoomTimeout);
    }
  },
})
</script>

<style scoped>
.visual_menu--section.visual_menu--section-animate_scale {
  transition: transform 250ms cubic-bezier(0.4, 0, 0.2, 1);
}
</style>